import Button from "@/components/bricks/button/Button";
import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon";
import type { ReactNode } from "react";

interface ReviewDetailLayoutProps {
  backLabel?: string;
  onBack: () => void;
  className?: string;
  loading?: boolean;
  error?: ReactNode;
  topPanel?: ReactNode;
  pdfPanel?: ReactNode;
  auditPanel?: ReactNode;
  infoPanel?: ReactNode;
  actionsPanel?: ReactNode;
  commentsPanel?: ReactNode;
}

const SkeletonLine = ({
  widthClass = "col-12",
  height = 16,
  className = "",
}: {
  widthClass?: string;
  height?: number;
  className?: string;
}) => (
  <span
    className={`placeholder ${widthClass} d-block ${className}`}
    style={{ height, minHeight: height }}
  />
);

const ReviewDetailLayoutSkeleton = () => (
  <div className="placeholder-glow">
    <div className="mt-3 c-card">
      <div className="d-flex flex-wrap gap-4">
        <SkeletonLine widthClass="col-3" className="rounded-2" height={30} />
        <SkeletonLine widthClass="col-2" className="rounded-2" height={30} />
        <SkeletonLine widthClass="col-2" className="rounded-2" height={30} />
      </div>
    </div>

    <div className="row gy-3 pb-4 mt-3">
      <div className="col-12 col-xl-6 d-flex flex-column gap-3">
        <div className="c-card">
          <SkeletonLine
            widthClass="col-6"
            height={24}
            className="mb-3 rounded-2"
          />
          <SkeletonLine widthClass="w-100" height={820} className="rounded-2" />
        </div>
        <div className="c-card">
          <SkeletonLine
            widthClass="col-4"
            height={24}
            className="mb-3 rounded-2"
          />
          <SkeletonLine widthClass="w-100" height={120} className="rounded-2" />
        </div>
      </div>
      <div className="col-12 col-xl-6 d-flex flex-column gap-3">
        <div className="c-card">
          <SkeletonLine
            widthClass="col-5"
            height={24}
            className="mb-3 rounded-2"
          />
          <div className="row">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="col-md-6 mb-3">
                <SkeletonLine
                  widthClass="col-8"
                  height={14}
                  className="mb-2 rounded-2"
                />
                <SkeletonLine
                  widthClass="col-10"
                  height={20}
                  className="rounded-2"
                />
              </div>
            ))}
          </div>
        </div>
        <div className="c-card">
          <SkeletonLine
            widthClass="col-4"
            height={24}
            className="mb-3 rounded-2"
          />
          <SkeletonLine widthClass="w-100" height={48} className="rounded-2" />
        </div>
        <div className="c-card">
          <SkeletonLine
            widthClass="col-4"
            height={24}
            className="mb-3 rounded-2"
          />
          <SkeletonLine widthClass="w-100" height={160} className="rounded-2" />
        </div>
      </div>
    </div>
  </div>
);

export const ReviewDetailLayout = ({
  backLabel = "Tilbage til efteruddannelse",
  onBack,
  className,
  loading = false,
  error,
  topPanel,
  pdfPanel,
  auditPanel,
  infoPanel,
  actionsPanel,
  commentsPanel,
}: ReviewDetailLayoutProps) => {
  const hasContent =
    pdfPanel || auditPanel || infoPanel || actionsPanel || commentsPanel;

  return (
    <div className={`container ${className}`}>
      <div className="d-flex align-items-center">
        <Button color="secondary" transparent type="button" onClick={onBack}>
          <ChevronLeftIcon />
          {backLabel}
        </Button>
      </div>

      {loading ? <ReviewDetailLayoutSkeleton /> : null}
      {!loading && error}
      {!loading && topPanel ? <div className="mt-3">{topPanel}</div> : null}

      {!loading && hasContent && (
        <div className="row gy-3 pb-4">
          <div className="col-12 col-xl-6 d-flex flex-column gap-3">
            {pdfPanel}
            {auditPanel}
          </div>
          <div className="col-12 col-xl-6 d-flex flex-column gap-3">
            {infoPanel}
            {actionsPanel}
            {commentsPanel}
          </div>
        </div>
      )}
    </div>
  );
};
